import { Head } from "@inertiajs/react"
import { Sparkles, Send, Loader2 } from "lucide-react"
import { useState, useRef } from "react"
import { useTranslation } from "react-i18next"
import ReactMarkdown from "react-markdown"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

interface ChatMessage {
  role: "user" | "assistant"
  content: string
}

export default function AssistantIndex() {
  const { t } = useTranslation()
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([])
  const [chatInput, setChatInput] = useState("")
  const [chatLoading, setChatLoading] = useState(false)
  const chatEndRef = useRef<HTMLDivElement>(null)

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: t("assistant.breadcrumb"),
      href: "/assistant",
    },
  ]

  const sendMessage = async () => {
    const message = chatInput.trim()
    if (!message || chatLoading) return

    const newMessages: ChatMessage[] = [
      ...chatMessages,
      { role: "user", content: message },
    ]
    setChatMessages(newMessages)
    setChatInput("")
    setChatLoading(true)

    try {
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content")
      const response = await fetch("/api/dashboard_suggestions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        body: JSON.stringify({
          message,
          conversation_history: newMessages,
          language: localStorage.getItem("i18nextLng") || "es",
        }),
      })
      const data = await response.json()
      setChatMessages([
        ...newMessages,
        {
          role: "assistant",
          content: data.response || t("assistant.chat.no_response"),
        },
      ])
    } catch {
      setChatMessages([
        ...newMessages,
        { role: "assistant", content: t("assistant.chat.error") },
      ])
    } finally {
      setChatLoading(false)
      setTimeout(
        () => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }),
        100,
      )
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={t("assistant.title")} />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        {/* Header */}
        <div>
          <div className="mb-2 flex items-center gap-3">
            <div className="bg-primary/10 flex h-12 w-12 items-center justify-center rounded-xl">
              <Sparkles className="text-primary h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">
                {t("assistant.page_title")}
              </h1>
              <p className="text-muted-foreground">{t("assistant.subtitle")}</p>
            </div>
          </div>
        </div>

        {/* Chat */}
        <Card className="glass border-primary/20 flex flex-1 flex-col">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Sparkles className="text-primary h-5 w-5" />
              {t("assistant.chat.placeholder")}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col">
            <div className="border-border/50 mb-4 min-h-[300px] flex-1 space-y-3 overflow-y-auto rounded-lg border p-4">
              {chatMessages.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center py-12 text-center">
                  <Sparkles className="text-primary/30 mb-4 h-12 w-12" />
                  <p className="text-muted-foreground text-sm">
                    {t("assistant.chat.empty_state")}
                  </p>
                  <div className="mt-4 space-y-2">
                    {[
                      t("assistant.suggestions.cupping_tips"),
                      t("assistant.suggestions.flavor_profiles"),
                      t("assistant.suggestions.scoring"),
                    ].map((suggestion) => (
                      <button
                        key={suggestion}
                        onClick={() => setChatInput(suggestion)}
                        className="text-foreground hover:text-primary block w-full text-left text-sm"
                      >
                        → {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {chatMessages.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg px-4 py-2 text-sm ${
                          msg.role === "user"
                            ? "bg-muted text-foreground"
                            : "bg-card border-border text-foreground border"
                        }`}
                      >
                        {msg.role === "user" ? (
                          <p className="whitespace-pre-wrap">{msg.content}</p>
                        ) : (
                          <div className="prose prose-sm dark:prose-invert max-w-none">
                            <ReactMarkdown
                              components={{
                                h1: ({ children }) => (
                                  <h1 className="mt-4 mb-2 text-lg font-bold">
                                    {children}
                                  </h1>
                                ),
                                h2: ({ children }) => (
                                  <h2 className="mt-3 mb-2 text-base font-bold">
                                    {children}
                                  </h2>
                                ),
                                h3: ({ children }) => (
                                  <h3 className="mt-2 mb-1 text-sm font-bold">
                                    {children}
                                  </h3>
                                ),
                                p: ({ children }) => (
                                  <p className="mb-2 last:mb-0">{children}</p>
                                ),
                                ul: ({ children }) => (
                                  <ul className="mb-2 list-disc space-y-1 pl-4">
                                    {children}
                                  </ul>
                                ),
                                ol: ({ children }) => (
                                  <ol className="mb-2 list-decimal space-y-1 pl-4">
                                    {children}
                                  </ol>
                                ),
                                li: ({ children }) => (
                                  <li className="text-sm">{children}</li>
                                ),
                                strong: ({ children }) => (
                                  <strong className="font-semibold">
                                    {children}
                                  </strong>
                                ),
                                code: ({ children }) => (
                                  <code className="bg-muted rounded px-1 py-0.5 text-xs">
                                    {children}
                                  </code>
                                ),
                              }}
                            >
                              {msg.content}
                            </ReactMarkdown>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="flex justify-start">
                      <div className="bg-accent text-accent-foreground flex items-center gap-2 rounded-lg px-4 py-2 text-sm">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {t("assistant.chat.loading")}
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </>
              )}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder={t("assistant.chat.placeholder")}
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                disabled={chatLoading}
              />
              <Button
                onClick={sendMessage}
                disabled={chatLoading || !chatInput.trim()}
                size="icon"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
